import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useCallback, useEffect, useId, useState } from 'react'
import { RedirectUrls } from 'src/types/types'

type Point = { icon: string; title: string; description: string }

type Variant = 'compact' | 'default' | 'overlay' | 'editorial'

interface VerifiedBadgeProps {
  /** Whether the listing is verified by SpotMe. If false, render nothing. */
  verified?: boolean | null
  /**
   * `compact`   = icon only (used inline in tight spaces).
   * `default`   = icon + label pill, blue-filled (legacy callers).
   * `editorial` = icon + label pill that matches the listing-header utility
   *               row (bordered, ink text, square corners). Use this when
   *               the badge sits next to other editorial pills (watching,
   *               fleet trigger) so the row reads as a unified group.
   * `overlay`   = absolute-positioned pill on top of an image (search cards).
   *   The parent must be `relative` for overlay positioning to work.
   */
  variant?: Variant
  /** Optional className override on the trigger element. */
  className?: string
}

/**
 * Trust badge for verified listings. Click → modal with what verification
 * actually means (host identity, background screening, listing review,
 * Chubb coverage availability). Renders nothing when `verified` is falsy.
 */
function VerifiedBadge({
  verified,
  variant = 'default',
  className
}: VerifiedBadgeProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  // Several badges render on one /search page — never share a fixed id.
  const titleId = useId()

  const handleOpen = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setOpen(true)
    },
    []
  )

  const handleClose = useCallback(() => setOpen(false), [])

  // Close on ESC
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!verified) return null

  const points = t('VERIFIED_BADGE.POINTS', {
    returnObjects: true
  }) as Point[]

  let trigger: React.ReactNode

  if (variant === 'compact') {
    trigger = (
      <button
        type="button"
        onClick={handleOpen}
        aria-label={t('VERIFIED_BADGE.ARIA')}
        className={`inline-flex items-center justify-center rounded-full text-blue-500 transition-colors hover:text-blue-700 ${className ?? ''}`}
      >
        <VerifiedRoundedIcon className="size-3.5" fontSize="inherit" />
      </button>
    )
  } else if (variant === 'overlay') {
    trigger = (
      <button
        type="button"
        onClick={handleOpen}
        aria-label={t('VERIFIED_BADGE.ARIA')}
        className={`absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white-500/95 px-2 py-0.5 text-[10px] font-medium tracking-[0.04em] text-ink-700 shadow-[0_2px_8px_-2px_rgba(18,38,63,0.25)] backdrop-blur-sm transition-colors hover:bg-white-500 ${className ?? ''}`}
      >
        <VerifiedRoundedIcon
          className="size-3 text-blue-500"
          fontSize="inherit"
        />
        {t('VERIFIED_BADGE.LABEL_SHORT')}
      </button>
    )
  } else if (variant === 'editorial') {
    // Matches the listing-header utility-row pill style (watching, fleet
    // trigger). Bordered, ink-toned label, square corners — reads as part
    // of the editorial pill group instead of a one-off chip.
    trigger = (
      <button
        type="button"
        onClick={handleOpen}
        aria-label={t('VERIFIED_BADGE.ARIA')}
        className={`inline-flex items-center gap-1.5 border border-blue-500/45 bg-blue-500/6 px-3 py-1.5 text-[11px] font-medium tracking-[0.08em] text-ink-700 transition-colors hover:bg-blue-500/10 md:text-[12px] ${className ?? ''}`}
      >
        <VerifiedRoundedIcon
          className="size-3.5 text-blue-500"
          fontSize="inherit"
        />
        {t('VERIFIED_BADGE.LABEL_SHORT')}
      </button>
    )
  } else {
    trigger = (
      <button
        type="button"
        onClick={handleOpen}
        aria-label={t('VERIFIED_BADGE.ARIA')}
        className={`inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-2.5 py-1 text-[11px] font-medium tracking-[0.04em] text-blue-500 transition-colors hover:bg-blue-500/15 md:text-[12px] ${className ?? ''}`}
      >
        <VerifiedRoundedIcon className="size-3.5" fontSize="inherit" />
        {t('VERIFIED_BADGE.LABEL_SHORT')}
      </button>
    )
  }

  return (
    <>
      {trigger}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-ink-700/60 px-4 backdrop-blur-sm md:items-center md:px-6"
          onClick={handleClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleClose()
          }}
        >
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-t-2xl bg-white-500 p-6 shadow-[0_24px_64px_-16px_rgba(18,38,63,0.4)] md:rounded-2xl md:p-8"
          >
            <button
              type="button"
              onClick={handleClose}
              aria-label={t('VERIFIED_BADGE.CLOSE')}
              className="absolute top-4 right-4 flex size-8 items-center justify-center rounded-full text-ink-200 transition-colors hover:bg-cream-300 hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
            >
              <i className="si si-close text-[16px]" aria-hidden />
            </button>

            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-2.5 py-1 text-[11px] font-medium tracking-[0.04em] text-blue-800">
              <VerifiedRoundedIcon className="size-3.5" fontSize="inherit" />
              {t('VERIFIED_BADGE.LABEL_LONG')}
            </span>

            <h2
              id={titleId}
              className="mt-4 font-display text-[22px] leading-tight font-medium text-ink-700 md:text-[26px]"
            >
              {t('VERIFIED_BADGE.MODAL_TITLE')}
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('VERIFIED_BADGE.MODAL_INTRO')}
            </p>

            <ul className="mt-6 flex flex-col gap-4 md:gap-5">
              {points.map((p) => (
                <li key={p.title} className="grid grid-cols-[auto_1fr] gap-3">
                  <span className="mt-0.5 flex size-8 items-center justify-center rounded-full bg-blue-500/10 text-blue-500">
                    <i className={`si ${p.icon} text-[16px]`} />
                  </span>
                  <div>
                    <h3 className="text-[14px] leading-tight font-medium text-ink-700 md:text-[15px]">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-[13px] leading-relaxed text-ink-200 md:text-[14px]">
                      {p.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-col gap-3 border-t border-ink-100/15 pt-5 md:mt-8 md:flex-row md:items-center md:justify-between">
              <Link
                href={RedirectUrls.Seguro}
                onClick={handleClose}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium tracking-[0.04em] text-blue-800 underline decoration-blue-500/40 underline-offset-[5px] transition-colors hover:decoration-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
              >
                {t('VERIFIED_BADGE.MORE_INFO_LINK')}
              </Link>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center justify-center rounded-full bg-blue-500 px-5 py-2 text-[13px] font-medium text-white-500 transition-colors hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
              >
                {t('VERIFIED_BADGE.CLOSE')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default React.memo(VerifiedBadge)
